import React, { useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import Layout from "../../components/layout/Layout";
import Dialog from "../../components/ui/Dialog";
import {
  useCancelSeminar,
  useCompleteSeminar,
  useDeleteSeminar,
  useRemoveParticipant,
  useSaveSeminarAttendance,
  useSeminar,
} from "../../api/queries/learningQueries";
import { getApiErrorMessage } from "../../utils/apiError";
import { formatDateTime } from "../../utils/dateTime";
import SeminarFormDialog from "./components/SeminarFormDialog";
import AssignParticipantsDialog from "./components/AssignParticipantsDialog";
import CheckInQrCard from "./components/CheckInQrCard";
import AttendanceSheetDialog from "./components/AttendanceSheetDialog";
import AttachmentsCard from "../../components/ui/AttachmentsCard";
import {
  useDeleteSeminarFile,
  useSeminarFiles,
  useUploadSeminarFile,
} from "../../api/queries/learningQueries";
import {
  PARTICIPANT_STATUS,
  PHASE,
  formatSchedule,
  titleCase,
} from "./components/learningFormat";
import "./learning.css";
import { useCanModify } from "../../hooks/usePermissions";

const STATUS_OPTIONS = ["PENDING", "ATTENDED", "ABSENT", "EXCUSED"];

const Meta = ({ label, children }) => (
  <div>
    <div className="approval-meta-label">{label}</div>
    <div className="learn-meta-value">{children || "—"}</div>
  </div>
);

/** Confirm dialog for cancel (reason), complete and delete. */
const ConfirmDialog = ({ kind, seminar, onClose, onDeleted }) => {
  const [reason, setReason] = useState("");
  const cancel = useCancelSeminar();
  const complete = useCompleteSeminar();
  const remove = useDeleteSeminar();
  const busy = cancel.isPending || complete.isPending || remove.isPending;
  const fail = (err) => {
    toast.error(getApiErrorMessage(err, "Could not do that."));
    onClose();
  };
  const run = () => {
    if (kind === "cancel")
      cancel.mutate(
        { id: seminar.id, reason: reason.trim() },
        {
          onSuccess: () => {
            toast.success("Seminar cancelled.");
            onClose();
          },
          onError: fail,
        },
      );
    if (kind === "complete")
      complete.mutate(seminar.id, {
        onSuccess: () => {
          toast.success("Seminar completed.");
          onClose();
        },
        onError: fail,
      });
    if (kind === "delete")
      remove.mutate(seminar.id, {
        onSuccess: () => {
          toast.success("Seminar deleted.");
          onDeleted();
        },
        onError: fail,
      });
  };
  const text = {
    cancel: [
      "Cancel seminar?",
      "Participants will see it as cancelled.",
      "Cancel seminar",
      "danger",
    ],
    complete: [
      "Mark as completed?",
      "Participants not yet marked become Absent.",
      "Complete",
      "success",
    ],
    delete: [
      "Delete seminar?",
      "This removes the seminar and its participants. It can't be undone.",
      "Delete",
      "danger",
    ],
  }[kind];
  return (
    <Dialog
      title={text[0]}
      subtitle={seminar.title}
      onClose={onClose}
      busy={busy}
      footer={
        <>
          <button
            type="button"
            className="btn btn-outline-secondary profile-btn"
            onClick={onClose}
            disabled={busy}
          >
            Close
          </button>
          <button
            type="button"
            className={`btn btn-${text[3]} profile-btn`}
            onClick={run}
            disabled={busy || (kind === "cancel" && !reason.trim())}
          >
            {busy && (
              <span
                className="spinner-border spinner-border-sm"
                aria-hidden="true"
              ></span>
            )}
            {text[2]}
          </button>
        </>
      }
    >
      <p className="fs-085">{text[1]}</p>
      {kind === "cancel" && (
        <>
          <label htmlFor="cancel-reason" className="profile-label">
            Reason <span className="text-danger">*</span>
          </label>
          <textarea
            id="cancel-reason"
            className="form-control"
            rows={2}
            maxLength={500}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            autoFocus
          />
        </>
      )}
    </Dialog>
  );
};

/**
 * One seminar: details, participants roster and attendance log.
 * Attendance can be logged once the seminar has started.
 */
const SEMINAR_FILE_CATEGORIES = [
  { value: "ATTENDANCE_SHEET", label: "Attendance sheet" },
  { value: "PHOTO", label: "Photo" },
  { value: "OTHER", label: "Other" },
];

const SeminarDetailPage = () => {
  // Learning: view-only roles can't edit, assign or log attendance
  const canModify = useCanModify("learning", { company: true });
  const { id } = useParams();
  const navigate = useNavigate();
  const query = useSeminar(id);
  const seminar = query.data;
  const save = useSaveSeminarAttendance();
  const removeParticipant = useRemoveParticipant();

  const [edits, setEdits] = useState({}); // { [employeeId]: { status, remarks } }
  const [dialog, setDialog] = useState(null); // edit | assign | cancel | complete | delete | sheet
  const filesQuery = useSeminarFiles(id);
  const uploadFile = useUploadSeminarFile();
  const deleteFile = useDeleteSeminarFile();
  const [search, setSearch] = useState("");

  const participants = useMemo(() => seminar?.participants || [], [seminar]);
  const filtered = participants.filter((p) => {
    const q = search.trim().toLowerCase();
    return (
      !q ||
      (p.name || "").toLowerCase().includes(q) ||
      (p.employeeNumber || "").toLowerCase().includes(q)
    );
  });

  const cancelled = seminar?.status === "CANCELLED";
  const started = seminar && seminar.phase !== "UPCOMING";
  const canLog = canModify && started && !cancelled;
  const dirtyCount = Object.keys(edits).length;
  const valueOf = (p) =>
    edits[p.employeeId] || { status: p.status, remarks: p.remarks || "" };

  const setEdit = (p, changes) =>
    setEdits((prev) => {
      const next = { ...valueOf(p), ...changes };
      const unchanged =
        next.status === p.status && (next.remarks || "") === (p.remarks || "");
      const copy = { ...prev };
      if (unchanged) delete copy[p.employeeId];
      else copy[p.employeeId] = next;
      return copy;
    });

  const markAllAttended = () =>
    participants.forEach(
      (p) =>
        valueOf(p).status === "PENDING" && setEdit(p, { status: "ATTENDED" }),
    );

  const saveAttendance = () =>
    save.mutate(
      {
        id,
        entries: Object.entries(edits).map(([employeeId, v]) => ({
          employeeId,
          status: v.status,
          remarks: v.remarks?.trim() || undefined,
        })),
      },
      {
        onSuccess: () => {
          toast.success(`Attendance saved for ${dirtyCount} participant(s).`);
          setEdits({});
        },
        onError: (err) =>
          toast.error(getApiErrorMessage(err, "Could not save attendance.")),
      },
    );

  const remove = (p) => {
    if (!window.confirm(`Remove ${p.name} from this seminar?`)) return;
    removeParticipant.mutate(
      { id, employeeId: p.employeeId },
      {
        onSuccess: () => toast.success(`${p.name} removed.`),
        onError: (err) =>
          toast.error(getApiErrorMessage(err, "Could not remove.")),
      },
    );
  };

  if (query.isLoading) {
    return (
      <Layout>
        <div className="text-center py-5">
          <span
            className="spinner-border text-primary"
            role="status"
            aria-label="Loading"
          ></span>
        </div>
      </Layout>
    );
  }
  if (query.isError || !seminar) {
    return (
      <Layout>
        <div className="container-fluid py-4 px-lg-4">
          <div className="alert alert-danger">
            {getApiErrorMessage(query.error, "Could not load this seminar.")}{" "}
            <Link to="/learning/seminars">Back</Link>
          </div>
        </div>
      </Layout>
    );
  }

  const phase = PHASE[seminar.phase] || { label: seminar.phase, tone: "gray" };
  const c = seminar.counts || {};

  return (
    <Layout>
      <div className="container-fluid py-4 px-lg-4">
        <div className="d-flex flex-wrap justify-content-between align-items-end gap-2 mb-3">
          <div className="min-w-0">
            <Link
              to="/learning/seminars"
              className="text-slate-500 fs-080 text-decoration-none"
            >
              <i className="bi bi-arrow-left"></i> Seminars
            </Link>
            <h1 className="fw-bold text-dark m-0 page-title text-truncate">
              {seminar.title}
            </h1>
            <div className="d-flex flex-wrap gap-2 align-items-center fs-080 text-slate-500 mt-1">
              <span>
                {titleCase(seminar.type)} · {titleCase(seminar.mode)}
              </span>
              {cancelled ? (
                <span className="status-pill status-pill--red">Cancelled</span>
              ) : (
                <span className={`status-pill status-pill--${phase.tone}`}>
                  {phase.label}
                </span>
              )}
              {seminar.status &&
                !cancelled &&
                seminar.status !== "SCHEDULED" && (
                  <span className="chip chip--sm">
                    {titleCase(seminar.status)}
                  </span>
                )}
            </div>
          </div>
          {!cancelled && canModify && (
            <div className="d-flex flex-wrap gap-2">
              <button
                type="button"
                className="btn profile-btn profile-btn-white"
                onClick={() => setDialog("edit")}
              >
                <i className="bi bi-pencil"></i> Edit
              </button>
              {started && (
                <button
                  type="button"
                  className="btn btn-outline-success profile-btn bg-white"
                  onClick={() => setDialog("complete")}
                >
                  <i className="bi bi-flag"></i> Complete
                </button>
              )}
              <button
                type="button"
                className="btn btn-outline-danger profile-btn bg-white"
                onClick={() => setDialog("cancel")}
              >
                <i className="bi bi-x-circle"></i> Cancel
              </button>
              {!started && (
                <button
                  type="button"
                  className="btn btn-outline-danger profile-btn bg-white"
                  onClick={() => setDialog("delete")}
                >
                  <i className="bi bi-trash"></i>
                </button>
              )}
            </div>
          )}
        </div>

        <div className="learn-card learn-meta-grid mb-3">
          <Meta label="Schedule">
            {formatSchedule(seminar.startAt, seminar.endAt)}
          </Meta>
          <Meta label="Hours">{seminar.hours ?? "—"}</Meta>
          {seminar.venue && <Meta label="Venue">{seminar.venue}</Meta>}
          {seminar.meetingLink && (
            <Meta label="Meeting link">
              <a
                href={seminar.meetingLink}
                target="_blank"
                rel="noopener noreferrer"
              >
                Join link
              </a>
            </Meta>
          )}
          <Meta label="Facilitator">{seminar.facilitator}</Meta>
          <Meta label="Branch">
            {seminar.branch?.branchName || seminar.branchName || "All branches"}
          </Meta>
          <Meta label="Seats">
            {seminar.capacity
              ? `${c.total ?? 0} / ${seminar.capacity}`
              : `${c.total ?? 0} (no limit)`}
          </Meta>
          <Meta label="Enrollment">
            {seminar.openEnrollment
              ? "Open: employees can sign up"
              : "Assigned by HR only"}
          </Meta>
          {(seminar.skills || []).length > 0 && (
            <div className="learn-meta-span">
              <div className="approval-meta-label">Skills</div>
              <div className="d-flex flex-wrap gap-1">
                {seminar.skills.map((k) => (
                  <span key={k.id} className="chip chip--sm">
                    {k.name}
                  </span>
                ))}
              </div>
            </div>
          )}
          {seminar.description && (
            <div className="learn-meta-span">
              <div className="approval-meta-label">Description</div>
              <div className="fs-085 white-space-pre">
                {seminar.description}
              </div>
            </div>
          )}
          {cancelled && seminar.cancelReason && (
            <div className="learn-meta-span">
              <div className="approval-note approval-note--rejected">
                Cancelled: {seminar.cancelReason}
              </div>
            </div>
          )}
        </div>

        <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-2">
          <h2 className="learn-subtitle m-0">
            Participants ({participants.length})
            <span className="fs-075 fw-normal text-slate-500 ms-2">
              {c.ATTENDED ?? 0} attended · {c.ABSENT ?? 0} absent ·{" "}
              {c.EXCUSED ?? 0} excused · {c.PENDING ?? 0} not marked
            </span>
          </h2>
          <div className="d-flex flex-wrap gap-2 align-items-center">
            <input
              type="search"
              className="filter-input"
              placeholder="Find participant"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              aria-label="Find participant"
            />
            {!cancelled && canModify && (
              <button
                type="button"
                className="btn btn-sm btn-outline-primary bg-white"
                onClick={() => setDialog("assign")}
              >
                <i className="bi bi-person-plus me-1"></i>Assign
              </button>
            )}
            {canLog && (
              <button
                type="button"
                className="btn btn-sm btn-outline-success bg-white"
                onClick={markAllAttended}
              >
                Mark all attended
              </button>
            )}
            {canLog && participants.length > 0 && (
              <button
                type="button"
                className="btn btn-sm btn-outline-primary bg-white"
                onClick={() => setDialog("sheet")}
              >
                <i className="bi bi-file-earmark-spreadsheet me-1"></i>Upload
                sheet
              </button>
            )}
          </div>
        </div>
        {!started && !cancelled && (
          <p className="fs-080 text-slate-500">
            Attendance can be logged once the seminar starts.
          </p>
        )}

        <div className="glass-table-card">
          <div className="table-responsive">
            <table className="table mb-0 learn-table">
              <thead className="custom-table-header">
                <tr>
                  <th className="ps-4 py-3">Employee</th>
                  <th className="py-3">Branch · Department</th>
                  <th className="py-3">Source</th>
                  <th className="py-3">Attendance</th>
                  <th className="py-3">Remarks</th>
                  <th className="py-3 pe-4 text-end" aria-label="Actions"></th>
                </tr>
              </thead>
              <tbody className="border-top-0">
                {filtered.map((p) => {
                  const v = valueOf(p);
                  const meta = PARTICIPANT_STATUS[v.status] || {
                    label: v.status,
                    tone: "gray",
                  };
                  const dirty = !!edits[p.employeeId];
                  return (
                    <tr
                      key={p.employeeId}
                      className={dirty ? "learn-row-dirty" : ""}
                    >
                      <td className="ps-4">
                        <div className="emp-name-text">{p.name}</div>
                        <div className="emp-sub-text">{p.employeeNumber}</div>
                      </td>
                      <td className="fs-075">
                        {p.branchName || "—"}
                        {p.departmentName ? ` · ${p.departmentName}` : ""}
                      </td>
                      <td className="fs-075">
                        {p.source === "SELF_ENROLLED"
                          ? "Self-enrolled"
                          : "Assigned"}
                      </td>
                      <td>
                        {canLog ? (
                          <select
                            className={`form-select form-select-sm learn-status learn-status--${meta.tone}`}
                            value={v.status}
                            onChange={(e) =>
                              setEdit(p, { status: e.target.value })
                            }
                            aria-label={`Attendance of ${p.name}`}
                          >
                            {STATUS_OPTIONS.map((s) => (
                              <option key={s} value={s}>
                                {PARTICIPANT_STATUS[s].label}
                              </option>
                            ))}
                          </select>
                        ) : (
                          <span
                            className={`status-pill status-pill--${meta.tone}`}
                          >
                            {meta.label}
                          </span>
                        )}
                        {p.markedAt && !dirty && (
                          <div className="fs-070 text-slate-500">
                            {formatDateTime(p.markedAt)}
                          </div>
                        )}
                      </td>
                      <td>
                        {canLog ? (
                          <input
                            className="form-control form-control-sm"
                            value={v.remarks || ""}
                            maxLength={300}
                            onChange={(e) =>
                              setEdit(p, { remarks: e.target.value })
                            }
                            aria-label={`Remarks for ${p.name}`}
                          />
                        ) : (
                          <span className="fs-075 text-slate-600">
                            {p.remarks || "—"}
                          </span>
                        )}
                      </td>
                      <td className="pe-4 text-end">
                        {!started && !cancelled && (
                          <button
                            type="button"
                            className="btn btn-sm btn-light text-danger"
                            onClick={() => remove(p)}
                            aria-label={`Remove ${p.name}`}
                            disabled={removeParticipant.isPending}
                          >
                            <i className="bi bi-x-lg"></i>
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
                {filtered.length === 0 && (
                  <tr>
                    <td colSpan={6} className="approval-empty">
                      <i className="bi bi-people"></i>
                      {participants.length
                        ? "No participant matches."
                        : "No participants yet. Use Assign to add employees."}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* QR check-in + signed sheets / photos */}
        <div className="row g-3 mt-1">
          <div className="col-lg-5">
            <CheckInQrCard
              seminar={seminar}
              canModify={canModify}
              cancelled={cancelled}
            />
          </div>
          <div className="col-lg-7">
            <div className="learn-card h-100">
              <AttachmentsCard
                title="Files"
                items={filesQuery.data || []}
                isLoading={filesQuery.isLoading}
                error={filesQuery.error}
                categories={SEMINAR_FILE_CATEGORIES}
                defaultCategory="ATTENDANCE_SHEET"
                accept=".pdf,.jpg,.jpeg,.png,.webp,.heic,.xlsx,.xls,.csv"
                acceptLabel="PDF, image, Excel or CSV"
                canModify={canModify}
                emptyText="No files yet (scanned signed attendance sheets, photos)."
                onUpload={(body) => uploadFile.mutateAsync({ id, ...body })}
                onDelete={(f) => deleteFile.mutateAsync({ id, fileId: f.id })}
              />
            </div>
          </div>
        </div>

        {dirtyCount > 0 && (
          <div className="learn-save-bar">
            <span className="fs-085">
              <strong>{dirtyCount}</strong> unsaved attendance change
              {dirtyCount === 1 ? "" : "s"}
            </span>
            <div className="d-flex gap-2">
              <button
                type="button"
                className="btn btn-sm btn-outline-secondary bg-white"
                onClick={() => setEdits({})}
                disabled={save.isPending}
              >
                Discard
              </button>
              <button
                type="button"
                className="btn btn-sm btn-primary"
                onClick={saveAttendance}
                disabled={save.isPending}
              >
                {save.isPending && (
                  <span
                    className="spinner-border spinner-border-sm me-1"
                    aria-hidden="true"
                  ></span>
                )}
                Save attendance
              </button>
            </div>
          </div>
        )}
      </div>

      {dialog === "edit" && (
        <SeminarFormDialog seminar={seminar} onClose={() => setDialog(null)} />
      )}
      {dialog === "assign" && (
        <AssignParticipantsDialog
          seminar={seminar}
          onClose={() => setDialog(null)}
        />
      )}
      {dialog === "sheet" && (
        <AttendanceSheetDialog
          seminar={seminar}
          onClose={() => setDialog(null)}
        />
      )}
      {["cancel", "complete", "delete"].includes(dialog) && (
        <ConfirmDialog
          kind={dialog}
          seminar={seminar}
          onClose={() => setDialog(null)}
          onDeleted={() => navigate("/learning/seminars")}
        />
      )}
    </Layout>
  );
};

export default SeminarDetailPage;
